import { CONVERSATIONS_API_URL, getConversationsAuthToken } from '@/config/env'

/**
 * Lo que devuelve DRF cuando algo no vale, en una forma que una pantalla pueda
 * usar: el mensaje entero para un aviso, y `fields` para ponerlo bajo su campo.
 */
export class DrfApiError extends Error {
  // Declarados y asignados a mano: `erasableSyntaxOnly` del tsconfig prohibe
  // los parametros-propiedad del constructor.
  status: number
  fields: Record<string, string[]>

  constructor(message: string, status: number, fields: Record<string, string[]> = {}) {
    super(message)
    this.name = 'DrfApiError'
    this.status = status
    this.fields = fields
  }
}

// Un serializador con `many=True` devuelve una lista de objetos, y uno anidado
// un objeto dentro de otro: los mensajes pueden estar a cualquier profundidad.
function collectMessages(value: unknown): string[] {
  if (typeof value === 'string') return value.trim() ? [value.trim()] : []
  if (Array.isArray(value)) return value.flatMap(collectMessages)
  if (value && typeof value === 'object') return Object.values(value).flatMap(collectMessages)
  return []
}

export function parseDrfError(payload: unknown, status: number, statusText = ''): DrfApiError {
  const fallback = statusText || `HTTP ${status}`
  if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) {
    return new DrfApiError(collectMessages(payload).join(' ') || fallback, status)
  }

  const record = payload as Record<string, unknown>
  // `{success: false, error: 'Invalid request data', details: {campo: [...]}}`: lo
  // útil son los detalles, y el `error` de encima no dice nada a quien lo lee.
  const details = record.details
  const fieldSource =
    details && typeof details === 'object' && !Array.isArray(details) ? (details as Record<string, unknown>) : record
  const direct = [record.detail, fieldSource === record ? record.error : null, fieldSource.non_field_errors].flatMap(
    collectMessages,
  )

  const fields: Record<string, string[]> = {}
  for (const [field, value] of Object.entries(fieldSource)) {
    if (field === 'detail' || field === 'error' || field === 'non_field_errors' || field === 'success') continue
    const messages = collectMessages(value)
    if (messages.length) fields[field] = messages
  }

  const fromFields = Object.entries(fields).map(([field, messages]) => `${field}: ${messages.join(' ')}`)
  const generic = collectMessages(record.error).join(' ')
  const message = direct.length ? direct.join(' ') : fromFields.join(' | ') || generic
  return new DrfApiError(message || fallback, status, fields)
}

// Las vistas de este servicio responden `{success, data}` y las páginas añaden
// `total`. Aquí se quita el sobre, para que un servicio solo vea lo que pidió: el
// objeto o `{count, results}`.
function unwrapEnvelope(payload: unknown): unknown {
  if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) return payload
  const record = payload as Record<string, unknown>
  if (typeof record.success !== 'boolean' || !('data' in record)) return payload
  if (Array.isArray(record.data) && typeof record.total === 'number') {
    return { count: record.total, results: record.data }
  }
  return record.data
}

/** Las peticiones al servicio de conversaciones con la sesión del dashboard. */
export async function drfRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = getConversationsAuthToken()
  // Con FormData, la cabecera la pone el navegador: es quien conoce el `boundary`.
  const isMultipart = options.body instanceof FormData

  let response: Response
  try {
    response = await fetch(`${CONVERSATIONS_API_URL || ''}${endpoint}`, {
      ...options,
      headers: {
        ...(isMultipart ? {} : { 'Content-Type': 'application/json' }),
        ...(token ? { Authorization: `Token ${token}` } : {}),
        ...options.headers,
      },
    })
  } catch (error) {
    // `status = 0` es lo que permite decir «sin conexión» y no «error del servidor».
    throw new DrfApiError(error instanceof Error ? error.message : String(error), 0)
  }

  if (!response.ok) {
    const payload: unknown = await response.json().catch(() => null)
    throw parseDrfError(payload, response.status, response.statusText)
  }

  if (response.status === 204) return undefined as T
  return unwrapEnvelope(await response.json()) as T
}
